import { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Paper,
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  PlayArrowOutlined,
  Router as RouterIcon,
  Save,
  SaveAs,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  allTestsPassed,
  emptyRouter,
  emptyTestsLocal,
  Router,
  RouterFormErrors,
  RouterPayload,
  ROUTER_MODES,
  TestsLocalState,
} from "../types";
import { validateRouter } from "../validation";
import { useModalStyles } from "../../components/modalStyles";
import styled from "styled-components";
import ConfigureRouter from "./configureRouter";
import ConnectivityTests from "./connectivityTests";

const QontoConnector = styled(StepConnector)(({ theme }: { theme: any }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
    },
  },
}));

interface ModalRouterProps {
  open: boolean;
  loading?: boolean;
  routerData?: Router | null;
  onClose: () => void;
  onSave: (payload: RouterPayload, id?: Router["id"]) => void;
}

export default function ModalRouter(props: ModalRouterProps) {
  const { open, loading = false, routerData, onClose, onSave } = props;

  const [form, setForm] = useState<RouterPayload>(emptyRouter);
  const [errors, setErrors] = useState<RouterFormErrors>({});

  const [activeStep, setActiveStep] = useState<number>(0);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testsLocal, setTestsLocal] =
    useState<TestsLocalState>(emptyTestsLocal);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const testsApproved = useMemo(() => allTestsPassed(testsLocal), [testsLocal]);

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(routerData?.id);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (activeStep === 2 && loading) {
      setHasSubmitted(true);
    }
    if (activeStep !== 2) {
      setHasSubmitted(false);
    }
  }, [activeStep, loading]);

  useEffect(() => {
    if (activeStep !== 1) setTestsLocal(emptyTestsLocal);
  }, [activeStep]);

  useEffect(() => {
    if (!open) return;

    let initialData: RouterPayload = emptyRouter;

    if (routerData) {
      const { id, ...rest } = routerData;
      initialData = { ...emptyRouter, ...rest };
    }

    setForm(initialData);
    setErrors(validateRouter(initialData));
    setActiveStep(0);
    setIsTesting(false);
    setTestsLocal(emptyTestsLocal);
  }, [open, routerData]);

  const handleChange = (field: keyof RouterPayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validateRouter(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validateRouter(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0 || !testsApproved) return;

    onSave(
      {
        ...form,
        name: form.name.trim(),
        sourceIp: form.sourceIp.trim(),
        snmpIp: form.snmpIp.trim(),
        snmpCommunity: form.snmpCommunity.trim(),
      },
      routerData?.id,
    );
  };

  const fields: {
    field: keyof RouterPayload;
    label: string;
    placeholder: string;
    size: { xs: number; sm: number };
    select?: boolean;
  }[] = [
    {
      field: "name",
      label: "Nome",
      placeholder: "Digite o nome do roteador",
      size: { xs: 12, sm: 12 },
    },
    {
      field: "sourceIp",
      label: "IP de origem",
      placeholder: "Digite o IP de origem",
      size: { xs: 12, sm: 6 },
    },
    {
      field: "mode",
      label: "Modo",
      placeholder: "Selecione o modo",
      size: { xs: 12, sm: 6 },
      select: true,
    },
    {
      field: "mostragemIpv4",
      label: "Amostragem IPv4",
      placeholder: "Digite a amostragem IPv4",
      size: { xs: 12, sm: 6 },
    },
    {
      field: "mostragemIpv6",
      label: "Amostragem IPv6",
      placeholder: "Digite a amostragem IPv6",
      size: { xs: 12, sm: 6 },
    },
    {
      field: "snmpIp",
      label: "Digite o IP SNMP",
      placeholder: "IP Snmp",
      size: { xs: 12, sm: 12 },
    },
    {
      field: "snmpCommunity",
      label: "Digite a comunidade SNMP",
      placeholder: "Comunidade",
      size: { xs: 12, sm: 8 },
    },
    {
      field: "snmpPort",
      label: "Porta SNMP",
      placeholder: "Porta SNMP",
      size: { xs: 12, sm: 4 },
    },
  ];

  const handleBack = () => {
    activeStep === 0
      ? onClose()
      : setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const steps = [
    {
      label: "Configuração",
      description: (
        <ConfigureRouter
          form={form}
          errors={errors}
          handleChange={handleChange}
          fields={fields}
          ROUTER_MODES={ROUTER_MODES}
          styles={styles}
        />
      ),
    },

    {
      label: "Testes de conectividade",
      description: (
        <ConnectivityTests
          form={form}
          onTestingChange={setIsTesting}
          setTestsLocal={setTestsLocal}
        />
      ),
    },
    {
      label: routerData ? "Edição" : "Cadastro",
      description: (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
          flex={1}
        >
          {!hasSubmitted || loading ? (
            <>
              <Stack
                sx={{
                  backgroundColor: theme.palette.background.textField,
                  borderRadius: "100%",
                  height: 56,
                  width: 56,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={26} />
              </Stack>
              <Typography variant="subtitle1" color="text.primary">
                {routerData ? "Editando" : "Cadastrando"} Roteador...
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Isso pode levar alguns segundos
              </Typography>
            </>
          ) : (
            <>
              <Stack
                sx={{
                  backgroundColor: alpha(theme.palette.error.main, 0.25),
                  borderRadius: "100%",
                  height: 46,
                  width: 46,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Close
                  sx={{
                    color: theme.palette.error.main,
                    height: 34,
                    width: 34,
                  }}
                />
              </Stack>
              <Typography variant="subtitle1" color="error.main">
                Ocorreu um erro ao {routerData ? "editar" : "cadastrar"} o
                roteador.
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Por favor, tente novamente
              </Typography>
              <Button variant="outlined" onClick={handleBack} sx={{ mt: 2 }}>
                Voltar
              </Button>
            </>
          )}
        </Box>
      ),
    },
  ];

  const handleNext = async () => {
    if (activeStep === 0) {
      if (hasErrors) {
        setErrors(errors);
        return;
      }
      setErrors({});
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    if (activeStep === 1) {
      if (isTesting || !testsApproved) return;
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      handleSave();
    }
  };

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={styles.paper}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RouterIcon color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar roteador" : "Adicionar roteador"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.name || "Novo roteador"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />
        <Stepper
          activeStep={activeStep}
          orientation="horizontal"
          connector={<QontoConnector theme={theme} />}
        >
          {steps.map(({ label }, index) => (
            <Step key={label}>
              <StepLabel>
                {isMobile && index !== activeStep ? "" : label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Divider sx={styles.divider} />
        <Stack sx={{ height: "100%", flex: 1 }}>
          {steps[activeStep].description}
        </Stack>
        {activeStep < 2 && (
          <>
            <Divider sx={styles.divider} />
            <Box
              sx={{
                ...styles.boxActions,
                justifyContent: "space-between",
                alignItems: "center",
                pt: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Etapa {activeStep + 1} de {steps.length}
              </Typography>

              <Box sx={styles.boxActions}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  size="medium"
                  disabled={loading || isTesting}
                  sx={{
                    color: "text.primary",
                    borderColor: alpha(theme.palette.text.primary, 0.25),
                    ":hover": {
                      borderColor: alpha(theme.palette.text.primary, 0.4),
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    },
                  }}
                >
                  {activeStep === 0 ? "Cancelar" : "Voltar"}
                </Button>

                <LoadingButton
                  loading={loading}
                  variant="contained"
                  size="medium"
                  onClick={handleNext}
                  disabled={
                    loading ||
                    isTesting ||
                    (activeStep === 1 ? !testsApproved : hasErrors)
                  }
                  startIcon={
                    activeStep === 0 ? (
                      <PlayArrowOutlined />
                    ) : routerData ? (
                      <SaveAs />
                    ) : (
                      <Save />
                    )
                  }
                >
                  {activeStep !== 1
                    ? "Iniciar testes"
                    : routerData
                      ? "Atualizar"
                      : "Cadastrar"}
                </LoadingButton>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Modal>
  );
}
